import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

const VIDEO_URL =
  "https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260428_193507_4286c423-2fd9-4efd-92bd-91a939453fc1.mp4";

const EASE = [0.2, 0.8, 0.2, 1] as const;

interface HeroProps {
  onDesigner?: () => void;
  onExplore?: () => void;
}

export default function Hero({ onDesigner, onExplore }: HeroProps) {
  const navigate = useNavigate();
  const goDesigner = () => (onDesigner ? onDesigner() : navigate("/designer"));
  const goExplore = () => (onExplore ? onExplore() : navigate("/explore"));


  return (
    <section
      className="relative min-h-screen w-full overflow-hidden bg-black"
      aria-labelledby="hero-title"
    >
      {/* Background video */}
      <video
        className="absolute inset-0 w-full h-full object-cover"
        src={VIDEO_URL}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden="true"
      />
      {/* Bright background, no dark overlay */}


      {/* Center content */}
      <div className="relative z-20 flex flex-col items-center justify-center text-center min-h-screen px-6 pt-24 sm:pt-28 pb-40">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
          className="inline-flex items-center gap-2 rounded-full border border-black/20 bg-white/10 backdrop-blur-md px-3.5 py-1.5 text-[12px] text-black mb-8"
        >
          <Sparkles className="w-3.5 h-3.5 text-black" />
          AI Interior Design Platform
        </motion.div>

        <motion.h1
          id="hero-title"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.25 }}
          className="max-w-[1000px] text-black font-semibold leading-[1.02] tracking-[-0.03em] text-[42px] sm:text-[64px] lg:text-[80px]"
        >
          Visualize Your Dream Room
          <br />
          Before You Renovate
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.45 }}
          className="mt-6 max-w-[680px] text-[15px] sm:text-[17px] leading-[1.65] text-black/80"
        >
          Upload a photo of your room and instantly generate photorealistic AI interior designs.
          Explore over 90 interior styles for bedrooms, living rooms, kitchens, bathrooms, offices,
          and more before making confident renovation decisions.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.6 }}
          className="mt-9"
        >
          <button
            onClick={goDesigner}
            className="group inline-flex items-center gap-2 rounded-full bg-white text-black px-7 py-3.5 text-[14px] font-medium hover:bg-white/90 transition-all shadow-[0_20px_60px_-20px_rgba(255,255,255,0.5)]"
          >
            Start Designing
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </motion.div>

        {/* App store badges */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.75 }}
          className="mt-8 flex items-center gap-3 sm:gap-4"
        >
          <a
            href="https://apps.apple.com/in/app/decor-canvas/id6758079242"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download Decor Canvas on the App Store"
            className="transition-transform hover:-translate-y-0.5"
          >
            <img
              src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
              alt="Download on the App Store"
              className="h-11 sm:h-12 w-auto"
            />
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=com.atheniaai.decorcanvas"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Get Decor Canvas on Google Play"
            className="transition-transform hover:-translate-y-0.5"
          >
            <img
              src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
              alt="Get it on Google Play"
              className="h-[62px] sm:h-[68px] w-auto -my-2"
            />
          </a>
        </motion.div>
      </div>

    </section>
  );
}
